import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from "../../../app/store";
import {
    CreateOfferRequest,
    Currency,
    OfferInListDTO,
    OfferSource,
    OfferStatus,
    OfferType,
    SubtractionType
} from "../../../api/generated";
import {v4} from "uuid";
import {offerApi, RequestStatus} from "../../../api/apiConfiguration";
import {selectExceptionally} from "../../../utils/selectExceptionally";

export const createOffer = createAsyncThunk<OfferInListDTO, undefined, { state: RootState }>(
    "offerCreate/create",
    (_payload, {getState}) => {
        const {offerCreate} = getState();
        const {createOfferRequest} = offerCreate;
        const request = mapRequest(createOfferRequest);
        return offerApi.createOffer({createOfferRequest: request});
    }
);

export type CreateOfferRequestPartial = Partial<CreateOfferRequest>

export interface CreateOfferState {
    createOfferRequest: CreateOfferRequestPartial
    createOfferRequestStatus: RequestStatus
}

const buildDefaultCreateOfferRequest = (): CreateOfferRequestPartial => {
    const startDate = new Date();

    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + 1);

    return {
        publicId: v4(),
        offerStatus: OfferStatus.Active,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        discount: {
            subtractionValue: 0,
            subtractionType: SubtractionType.Number
        },
        offerSource: OfferSource.Manual,
        offerSourceName: "Platform",
    }
}

const buildInitialState = (): CreateOfferState => {
    return {
        createOfferRequest: buildDefaultCreateOfferRequest(),
        createOfferRequestStatus: RequestStatus.NONE
    }
}

const initialState: CreateOfferState = buildInitialState()

export const offerCreateSlice = createSlice({
    name: 'offerCreate',
    initialState,
    reducers: {
        clearCreateOfferState: () => {
            return buildInitialState();
        },
        updateCreateOfferRequest: (state, {payload}: PayloadAction<CreateOfferRequestPartial>) => {
            if (payload.offerType && payload.offerType === OfferType.NewVehicle && !state.createOfferRequest?.currency) {
                payload = {
                    ...payload,
                    currency: Currency.Gbp
                }
            }
            state.createOfferRequest = {
                ...state.createOfferRequest,
                ...payload
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(createOffer.pending, (state) => {
            state.createOfferRequestStatus = RequestStatus.PENDING;
        })
        builder.addCase(createOffer.fulfilled, (state) => {
            state.createOfferRequestStatus = RequestStatus.RESOLVED;
        })
        builder.addCase(createOffer.rejected, (state) => {
            state.createOfferRequestStatus = RequestStatus.REJECTED;
            state.createOfferRequest.publicId = v4();
        })
    },
});

const mapRequest = (request: CreateOfferRequestPartial): CreateOfferRequest => {
    return {
        publicId: selectExceptionally(request.publicId),
        publicBotId: request.publicBotId,
        publicLocationId: request.publicLocationId,
        offerStatus: selectExceptionally(request.offerStatus),
        archivedAt: request.archivedAt,
        offerType: selectExceptionally(request.offerType),
        offerSource: selectExceptionally(request.offerSource),
        offerSourceName: selectExceptionally(request.offerSourceName),
        title: selectExceptionally(request.title),
        description: selectExceptionally(request.description),
        make: selectExceptionally(request.make),
        model: selectExceptionally(request.model),
        startDate: selectExceptionally(request.startDate),
        endDate: selectExceptionally(request.endDate),
        redirectUrl: selectExceptionally(request.redirectUrl),
        imageUrl: selectExceptionally(request.imageUrl),
        discount: request.discount,
        currency: request.currency,
        financeProduct: request.financeProduct,
        cashPrice: request.cashPrice,
        monthlyPayments: request.monthlyPayments,
        customerDeposit: request.customerDeposit,
        manufacturerDepositContribution: request.manufacturerDepositContribution,
        totalDeposit: request.totalDeposit,
        dealerContribution: request.dealerContribution,
        balanceToFinance: request.balanceToFinance,
        totalAmountPayable: request.totalAmountPayable,
        totalChargeForCredit: request.totalChargeForCredit,
        optionalFinalPayment: request.optionalFinalPayment,
        durationOfAgreementInMonths: request.durationOfAgreementInMonths,
        apr: request.apr,
        fixedInterestRate: request.fixedInterestRate,
        mileagePerAnnum: request.mileagePerAnnum,
        excessMileageCharge: request.excessMileageCharge
    }
}

export const {
    clearCreateOfferState,
    updateCreateOfferRequest
} = offerCreateSlice.actions;

export const createOfferRequestSelector = (state: RootState): CreateOfferRequestPartial => state.offerCreate.createOfferRequest;
export const createOfferRequestStatusSelector = (state: RootState): RequestStatus => state.offerCreate.createOfferRequestStatus;
